// frontend-react/src/components/CategoryManagement.js - CORREGIR

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete, AutoAwesome } from "@mui/icons-material";
import { adminService } from "../api/services";

const CategoryManagement = ({ categories, onCategoriesUpdate }) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    color: "#8A2BE2",
    is_featured: false,
    order: 0, // Inicializar con 0 en lugar de string vacío
  });

  // Crear categoría "Variado" por defecto si no existe
  const createDefaultCategory = async () => {
    try {
      const response = await adminService.createDefaultCategory();
      setMessage("Categoría 'Variado' creada exitosamente");
      onCategoriesUpdate();
    } catch (err) {
      console.error("Error creando categoría Variado:", err);
      setError(
        "Error creando categoría 'Variado': " +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || "",
        color: category.color || "#8A2BE2",
        is_featured: category.is_featured || false,
        order: category.order || 0, // Asegurar que sea número
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        slug: "",
        description: "",
        color: "#8A2BE2",
        is_featured: false,
        order: 0, // Inicializar con 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
  };

  const handleSubmit = async () => {
    try {
      console.log("🔄 CategoryManagement: handleSubmit started");

      // Validar campos requeridos
      if (!formData.name.trim()) {
        setError("El nombre de la categoría es requerido");
        return;
      }

      // Preparar datos - asegurar que el slug sea único
      const submitData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description || "",
        color: formData.color,
        is_featured: formData.is_featured,
        order: parseInt(formData.order) || 0,
      };

      if (editingCategory) {
        await adminService.updateCategory(editingCategory.id, submitData);
        setMessage("Categoría actualizada exitosamente");
      } else {
        await adminService.createCategory(submitData);
        setMessage("Categoría creada exitosamente");
      }

      onCategoriesUpdate();
      handleCloseDialog();
    } catch (err) {
      console.error("❌ CategoryManagement: Error in handleSubmit:", err);

      // Mostrar error detallado del backend
      const backendError = err.response?.data;
      console.log("Backend error details:", backendError);

      if (backendError) {
        // Mostrar errores de validación específicos
        if (typeof backendError === "object") {
          const errorMessages = Object.values(backendError).flat();
          setError("Error de validación: " + errorMessages.join(", "));
        } else {
          setError(
            "Error del servidor: " +
              (backendError.error || JSON.stringify(backendError))
          );
        }
      } else {
        setError("Error de conexión: " + (err.message || "Error desconocido"));
      }
    }
  };
  const handleDelete = async (category) => {
    if (
      window.confirm(
        `¿Estás seguro de eliminar la categoría "${category.name}"? Los posts serán movidos a "Variado".`
      )
    ) {
      try {
        await adminService.deleteCategory(category.id);
        setMessage(
          `Categoría "${category.name}" eliminada. Posts reasignados a "Variado".`
        );
        onCategoriesUpdate();
      } catch (err) {
        console.error("Error eliminando categoría:", err);
        setError(
          "Error eliminando categoría: " +
            (err.response?.data?.error || err.message)
        );
      }
    }
  };

  const handleGenerateViralCategories = async () => {
    try {
      setMessage("🚀 Generando categorías virales con IA...");
      const response = await adminService.generateViralCategories({ count: 5 });

      setMessage(`✅ ${response.data.message}`);
      onCategoriesUpdate(); // Refresca la lista
    } catch (err) {
      setError("Error generando categorías: " + err.response?.data?.error);
    }
  };

  const handleToggleFeatured = async (category) => {
    try {
      await adminService.updateCategory(category.id, {
        is_featured: !category.is_featured,
      });
      setMessage(
        `Categoría ${
          !category.is_featured ? "mostrada" : "ocultada"
        } en el header`
      );
      onCategoriesUpdate();
    } catch (err) {
      console.error("Error actualizando categoría:", err);
      setError(
        "Error actualizando categoría: " +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const handleUpdateOrder = async (category, newOrder) => {
    try {
      const orderValue = parseInt(newOrder) || 0;
      await adminService.updateCategory(category.id, {
        order: orderValue,
      });
      setMessage("Orden actualizado");
      onCategoriesUpdate();
    } catch (err) {
      console.error("Error actualizando orden:", err);
      setError(
        "Error actualizando orden: " +
          (err.response?.data?.error || err.message)
      );
    }
  };

  // Verificar si existe categoría "Variado"
  const hasVariadoCategory = categories.some((cat) => cat.slug === "variado");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6">Gestión de Categorías</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!hasVariadoCategory && (
            <Button
              variant="outlined"
              onClick={createDefaultCategory}
              color="warning"
            >
              Crear "Variado"
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AutoAwesome />}
            onClick={handleGenerateViralCategories}
          >
            Generar Categorías Virales
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nueva Categoría
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Paper sx={{ p: 2, position: "relative" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {category.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(category)}
                    disabled={category.slug === "variado"} // No permitir eliminar "Variado"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>

              <Chip
                label={category.slug}
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              />

              <Box
                sx={{ display: "flex", alignItems: "center", mb: 1, gap: 2 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={category.is_featured || false}
                      onChange={() => handleToggleFeatured(category)}
                      size="small"
                    />
                  }
                  label="En Header"
                />
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: category.color || "#8A2BE2",
                    borderRadius: 1,
                    border: "1px solid #ccc",
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Orden:</Typography>
                <TextField
                  type="number"
                  value={category.order || 0}
                  onChange={(e) => handleUpdateOrder(category, e.target.value)}
                  size="small"
                  sx={{ width: "80px" }}
                  inputProps={{ min: 0 }}
                />
              </Box>

              {category.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {category.description}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Diálogo para crear/editar categoría */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? "Editar Categoría" : "Nueva Categoría"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre *"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            error={!formData.name.trim()}
            helperText={!formData.name.trim() ? "El nombre es requerido" : ""}
          />
          <TextField
            label="Slug"
            fullWidth
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            margin="normal"
            helperText="Identificador único para URLs (se genera automáticamente si está vacío)"
          />
          <TextField
            label="Descripción"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            margin="normal"
          />
          <TextField
            label="Color"
            type="color"
            fullWidth
            value={formData.color}
            onChange={(e) =>
              setFormData({ ...formData, color: e.target.value })
            }
            margin="normal"
          />
          <TextField
            label="Orden"
            type="number"
            fullWidth
            value={formData.order}
            onChange={(e) => {
              const value = e.target.value;
              setFormData({
                ...formData,
                order: value === "" ? 0 : parseInt(value),
              });
            }}
            margin="normal"
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_featured}
                onChange={(e) =>
                  setFormData({ ...formData, is_featured: e.target.checked })
                }
              />
            }
            label="Mostrar en Header"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingCategory ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
